import React, { useState, useCallback, useEffect } from 'react';
import { useSessionMonitor } from '../hooks/useSessionMonitor';
import { AlertCircle, LogOut } from 'lucide-react';

/**
 * Stops all media playback on the page
 * - Pauses all video elements
 * - Pauses all audio elements
 * - Mutes everything
 */
const stopAllMedia = () => {
  // Stop all video elements
  const videos = document.querySelectorAll('video');
  videos.forEach(video => {
    try {
      video.pause();
      video.muted = true;
      video.currentTime = 0;
      // Remove the source to stop any streaming
      video.src = '';
      video.load();
    } catch (e) {
      console.error('Error stopping video:', e);
    }
  });

  // Stop all audio elements
  const audios = document.querySelectorAll('audio');
  audios.forEach(audio => {
    try {
      audio.pause();
      audio.muted = true;
      audio.currentTime = 0;
      audio.src = '';
      audio.load();
    } catch (e) {
      console.error('Error stopping audio:', e);
    }
  });

  // Stop any iframe videos (YouTube, Vimeo, Cloudflare Stream, etc.)
  const iframes = document.querySelectorAll('iframe');
  iframes.forEach(iframe => {
    try {
      // Remove src to stop playback
      const src = iframe.src;
      iframe.src = '';
      // For YouTube/Vimeo, we can try postMessage
      if (src.includes('youtube') || src.includes('vimeo')) {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
      }
    } catch (e) {
      console.error('Error stopping iframe:', e);
    }
  });

  // Stop Web Audio API contexts if any
  if (window.AudioContext || window.webkitAudioContext) {
    try {
      // Find and suspend any audio contexts
      const audioContexts = window._audioContexts || [];
      audioContexts.forEach(ctx => {
        if (ctx && ctx.state === 'running') {
          ctx.suspend();
        }
      });
    } catch (e) {
      console.error('Error suspending audio context:', e);
    }
  }

  // Set global mute flag that components can check
  window._sessionTerminated = true;
};

/**
 * Global component that monitors for session invalidation
 * Shows a modal when another device logs in with the same credentials
 * Stops all media playback when triggered
 */
const SessionMonitor = () => {
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');

  const handleSessionInvalidated = useCallback((msg) => {
    // IMMEDIATELY stop all media playback
    stopAllMedia();
    
    // Show the modal
    setMessage(msg || 'Your session has been terminated because someone logged in with your credentials on another device.');
    setShowModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowModal(false);
    
    // Clear session terminated flag
    window._sessionTerminated = false;
    
    // Redirect to appropriate login page
    const currentPath = window.location.pathname;
    if (currentPath.includes('/admin')) {
      window.location.href = '/admin';
    } else {
      window.location.href = '/live/login';
    }
  }, []);

  // When modal is shown, also add a style to hide video content
  useEffect(() => {
    if (showModal) {
      // Add a class to body to help hide sensitive content
      document.body.classList.add('session-terminated');
      
      // Continuously try to stop media in case of lazy loading
      const interval = setInterval(stopAllMedia, 500);
      
      return () => {
        clearInterval(interval);
        document.body.classList.remove('session-terminated');
      };
    }
  }, [showModal]);

  // Initialize session monitoring
  useSessionMonitor(handleSessionInvalidated);

  if (!showModal) return null;

  return (
    <>
      {/* Overlay that covers everything including video */}
      <div className="fixed inset-0 z-[9998] bg-black" />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="bg-gradient-to-b from-gray-900 to-black border border-red-500/50 rounded-2xl p-6 max-w-md w-full shadow-2xl shadow-red-500/20 animate-in fade-in zoom-in duration-300">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-white text-center mb-2">
            Session Terminated
          </h2>

          {/* Message */}
          <p className="text-gray-400 text-center mb-6">
            {message}
          </p>

          {/* Info box */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6">
            <p className="text-red-400 text-sm text-center">
              Only one active session is allowed per account. If this wasn't you, please change your password immediately.
            </p>
          </div>

          {/* Button */}
          <button
            onClick={handleClose}
            className="w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
          >
            <LogOut className="w-5 h-5" />
            Return to Login
          </button>
        </div>
      </div>
    </>
  );
};

export default SessionMonitor;
